import { Link, useNavigate } from "react-router-dom";

const FOOTER_LINKS = {
  Workers: [
    { label: "Find Work", to: "/find-work" },
    { label: "How It Works", to: "/how-it-works" },
    { label: "Capability Builder", to: "/capabilities" },
    { label: "Income Dashboard", to: "/dashboard" },
    { label: "Pricing Plans", to: "/pricing" },
  ],
  Employers: [
    { label: "Post a Job", to: "/hire" },
    { label: "Hire Now", to: "/hire" },
    { label: "Pricing & Plans", to: "/pricing" },
    { label: "For Teams", to: "/pricing" },
    { label: "API Access", to: "/pricing" },
  ],
  Company: [
    { label: "About Gig n' Cash", to: "/about" },
    { label: "Blog", to: "#" },
    { label: "Press", to: "#" },
    { label: "Careers", to: "#" },
    { label: "Contact", to: "#" },
  ],
  Legal: [
    { label: "Privacy Policy", to: "/legal?tab=privacy" },
    { label: "Terms of Service", to: "/legal?tab=terms" },
    { label: "Cookie Policy", to: "/legal?tab=cookies" },
    { label: "Accessibility", to: "/legal?tab=accessibility" },
  ],
};

const SOCIALS = [
  { icon: "ri-twitter-x-line", href: "#", label: "Twitter" },
  { icon: "ri-instagram-line", href: "#", label: "Instagram" },
  { icon: "ri-linkedin-box-line", href: "#", label: "LinkedIn" },
  { icon: "ri-facebook-circle-line", href: "#", label: "Facebook" },
  { icon: "ri-tiktok-line", href: "#", label: "TikTok" },
];

const TRUST_BADGES = [
  { icon: "ri-shield-check-line", text: "256-bit SSL Secured" },
  { icon: "ri-shield-user-line", text: "Privacy Protected" },
  { icon: "ri-lock-2-line", text: "Data Encrypted" },
  { icon: "ri-award-line", text: "4.9 ⭐ Rated Platform" },
];

export default function Footer() {
  const navigate = useNavigate();

  return (
    <footer className="bg-slate-900 text-white">
      {/* Top CTA Band */}
      <div className="bg-emerald-500 px-4 py-10">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="text-2xl font-extrabold text-white mb-1">Ready to start earning today?</h3>
            <p className="text-emerald-50 text-sm">Join 12,000+ workers already making money on Gig <span className="italic">n&apos;</span> Cash.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              to="/find-work"
              className="px-7 py-3 bg-white text-emerald-700 font-bold rounded-xl text-sm hover:bg-emerald-50 transition-colors cursor-pointer whitespace-nowrap inline-flex items-center"
            >
              <i className="ri-search-line mr-2" />
              Find Work Now
            </Link>
            <button
              onClick={() => navigate("/pricing")}
              className="px-7 py-3 bg-white/10 border border-white/20 text-white font-bold rounded-xl text-sm hover:bg-white/20 transition-colors cursor-pointer whitespace-nowrap"
            >
              View Pricing
            </button>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-10 mb-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <Link to="/">
              <img
                src="https://static.readdy.ai/image/8b9b321cd7d670b6a60b0d1b6baf2111/e3a635c31b7543fa0ee8e8d84db9d640.png"
                alt="Gig n' Cash"
                className="h-20 w-auto object-contain mb-4"
              />
            </Link>
            <p className="text-slate-400 text-sm leading-relaxed mb-6 max-w-xs">
              AI-powered income platform connecting real capabilities to real opportunities. Find work or hire workers — instantly.
            </p>
            {/* Socials */}
            <div className="flex items-center gap-3">
              {SOCIALS.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  aria-label={s.label}
                  rel="nofollow"
                  className="w-9 h-9 flex items-center justify-center rounded-lg bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-white transition-colors cursor-pointer"
                >
                  <i className={`${s.icon} text-base`} />
                </a>
              ))}
            </div>
          </div>

          {/* Link Columns */}
          {Object.entries(FOOTER_LINKS).map(([title, links]) => (
            <div key={title}>
              <h4 className="text-xs font-extrabold uppercase tracking-widest text-slate-400 mb-4">{title}</h4>
              <ul className="flex flex-col gap-2.5">
                {links.map((link) => (
                  <li key={link.label}>
                    {link.to === "#" ? (
                      <span className="text-sm text-slate-400 hover:text-white transition-colors cursor-pointer">
                        {link.label}
                      </span>
                    ) : (
                      <Link
                        to={link.to}
                        className="text-sm text-slate-400 hover:text-white transition-colors"
                      >
                        {link.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Trust Badges */}
        <div className="flex flex-wrap gap-4 mb-10 py-8 border-t border-slate-800">
          {TRUST_BADGES.map((badge) => (
            <div key={badge.text} className="flex items-center gap-2 text-slate-500 text-xs">
              <i className={`${badge.icon} text-slate-400`} />
              <span>{badge.text}</span>
            </div>
          ))}
        </div>

        {/* Bottom Bar */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-6 border-t border-slate-800">
          <p className="text-slate-500 text-xs">
            &copy; {new Date().getFullYear()} Gig <span className="italic">n&apos;</span> Cash Inc. All rights reserved.
          </p>
          <div className="flex items-center gap-2 text-slate-500 text-xs">
            <span className="w-2 h-2 flex items-center justify-center">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            </span>
            <span>All systems operational</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
